import {useIsLoggedIn} from 'modules/user/hooks/useIsLoggedIn';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {
    useAddToWishListMutation,
    useGetWishListArticlesQuery,
    useRemoveFromWishListMutation,
} from 'modules/wish-list/services';
import {useCallback, useMemo} from 'react';
import {showAuthDialog} from 'modules/auth/thunks';
import {IProductShort} from 'modules/products/types';
import {IAnalyticsMeta} from 'core/types';
import {IProductSnippet} from 'shared/types/productSnippet.types';

type TUseAddToWishlistParams = {
    analyticsData?: IAnalyticsMeta;
    productData: Pick<IProductShort, 'id' | 'name' | 'sections' | 'price' | 'properties'>;
    onAddToWishlistClick: () => void;
    onRemoveFromWishlistClick: () => void;
};

export const useWishlist = (params: TUseAddToWishlistParams): IProductSnippet['wishlist'] => {
    const {analyticsData, productData, onAddToWishlistClick, onRemoveFromWishlistClick} = params;
    const id = productData.id;

    const isLoggedIn = useIsLoggedIn();
    const dispatch = useAppDispatch();

    const {data} = useGetWishListArticlesQuery(undefined, {skip: !isLoggedIn});
    const [addToWishList, {isLoading: isAddLoading}] = useAddToWishListMutation();
    const [removeFromWishList, {isLoading: isRemoveLoading}] = useRemoveFromWishListMutation();

    const isInWishList = useMemo(() => Boolean(data?.items?.find((item) => item === id)), [data?.items, id]);

    const onAdd = useCallback(() => {
        if (!isLoggedIn) {
            dispatch(showAuthDialog);
        } else if (!isInWishList) {
            addToWishList({id}).then(() => {
                onAddToWishlistClick();
            });
        }
    }, [isLoggedIn, isInWishList, dispatch, addToWishList, id, onAddToWishlistClick]);

    const onRemove = useCallback(() => {
        if (!isLoggedIn) {
            dispatch(showAuthDialog);
        } else if (isInWishList) {
            removeFromWishList({id}).then(() => onRemoveFromWishlistClick());
        }
    }, [isLoggedIn, isInWishList, dispatch, removeFromWishList, id, onRemoveFromWishlistClick]);

    return {
        onAdd,
        onRemove,
        isPending: isAddLoading || isRemoveLoading,
        isAdded: isInWishList,
        positionIndex: analyticsData?.positionIndex,
    };
};
